import _ = require('underscore');

import FormGroup from "./form/formGroup";
import {Form} from "./form/form";

export interface QuestionaryRandomization {
    groupPermutations: number[][],
    formNumbers: { [formNumber:string]: string }
}

interface Jump {
    toFormId: string,
    group: FormGroup
}

/**
 * FormGroups randomization.
 * Randomizes FormGroups and Forms within a FormGroup
 */
export default class QuestionaryRandomizer{

    private jumps:{ [formId: string]: Jump[] } = {};

    constructor(
        private rootFormGroup: FormGroup,
        private groupPermutations: number[][] = [],
        private formNumbers?: { [formNumber:string]: string }){
    }

    translateMove(fromForm: Form|null, toFormId: string|null): string|null {

        // Jump only if from outside of the randomized form group, in this case goTo to first form of group means goTo
        // to group itself.
        // Jumps are transitive.

        let parents = fromForm ? this.getFormParents(fromForm) : [];
        let finalFormId = toFormId;
        while (finalFormId) {
            toFormId = finalFormId;
            let firstAllowedJump = _.find(
                this.jumps[toFormId],
                j => ! _.contains(parents, j.group));

            finalFormId = firstAllowedJump ? firstAllowedJump.toFormId : null;
        }

        return toFormId;
    }

    process(): QuestionaryRandomization{
        var i = 0;
        var randomizeGroups = (children: any[], topLevel: any) => {
            _(children).each(c => {
                if (c.tag === 'formGroup') {
                    c.id = i;   // TODO: use map instead of this marking
                    i++;
                    this.processGroup(c);
                    randomizeGroups(c.children, topLevel && !c.randomize);
                    if (topLevel && c.randomize) {
                        this.correctRandomizedFormGroupNumbers(c);
                    }
                }
            });
        };
        randomizeGroups([this.rootFormGroup], true);

        return {
            groupPermutations: this.groupPermutations,
            formNumbers: this.formNumbers ?? {},
        }
    }

    private getFormParents(form: Form) {
        var parents: FormGroup[] = [];
        var parent = form && form.parent;
        while (parent) {
            parents.push(parent);
            parent = parent.parent;
        }
        return parents;
    }

    private processGroup(g:FormGroup) {

        // assumptions about randomized group:
        // - no jumps inside / into except first form (means jump to group)
        // - last form nextForm is considered nextForm of new last form

        if (g.randomize) {
            // generate or read permutation
            const groupId = Number(g.id);
            let r: number[];
            if (this.groupPermutations[groupId]?.length !== g.children.length) {
                // generate new permutation, if permutation does not exists, or missing, or does not fit group length
                r = this.groupPermutations[groupId] = _.shuffle<number>(_.range(g.children.length));
            } else {
                // loaded permutation is ok
                r = this.groupPermutations[groupId];
            }


            // jump in
            const first = g.children[r[0]];
            const jumpTo = first.firstId!;
            const previousFirst = g.children[0];
            const jumpFrom = previousFirst.firstId!;
            if (jumpTo !== jumpFrom) {
                this.jumps[jumpFrom] = this.jumps[jumpFrom] || [];
                this.jumps[jumpFrom].push({
                    toFormId: jumpTo,
                    group: g,
                })
            }

            // jump out
            const last = _(g.children).last();
            const nextForm = last.nextFormId;
            g.children[_.last(r)].nextFormId = nextForm;

            // jumps within group
            const insideIds = g.children.map(f => f.firstId);
            insideIds.push(nextForm);
            for (var j = 0; j < r.length - 1; j++) {
                const current = g.children[r[j]];
                if (_.contains(insideIds, current.nextFormId)) {
                    const next = g.children[r[j + 1]];
                    current.nextFormId = next.firstId;
                }
            }
        }
    }

    private correctRandomizedFormGroupNumbers (formGroup: FormGroup) {
        var numbers = _(this.getFlattenForms(formGroup, false)).pluck('number');
        var idsRandomized = _(this.getFlattenForms(formGroup, true)).pluck('id');

        var mapping = _.object(idsRandomized, numbers);

        this.formNumbers = _.extend(this.formNumbers, mapping);
    }

    private getFlattenForms(group: FormGroup, randomized: boolean): Form[] {
        var list = group.children;
        if (randomized && group.randomize) {
            list = _(list).map((c, j) => {
                // @ts-ignore I hope I know what I am doing...
                return list[this.groupPermutations[group.id][j]];
            });
        }
        return _.flatten(
            _(list).map(c => {
                if ((c as FormGroup).tag === 'formGroup') {
                    return this.getFlattenForms(c as FormGroup, randomized);
                } else {
                    return c;
                }
            })
        );
    }
}